import React, { useEffect, useMemo, useState } from 'react';

import Axios from 'axios';

import { useUserPermission } from '@contexts/UserContext';

import { Loading } from '@components/Gui/Loading';
import { NwCard } from '@components/Gui/NwCard/NwCard';
import NwIcon from '@components/Gui/NwIcon';
import SystemMessage from '@components/Utils/SystemMessage';

import { useStatementContext, useStatementDispatch } from './StatementContext';
import StatementTabs from './StatementTabs';
import { Container } from './StatementUI';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const CloseIconStyle = {
    marginLeft: '1rem',
};

/**
 * StatementCard
 *
 * Main card component for displaying statements.
 * Handles data loading for statements and payments.
 * Universal component that handles all contact types (model, client, agency, service)
 * internally without type-specific sub-components.
 */
const StatementCard = ({ onClose }) => {
    const { statementType, contactType, contactID, apiUrl, paymentsApiUrl, reload } = useStatementContext();
    const dispatch = useStatementDispatch();

    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [isStatementFetching, setIsStatementFetching] = useState(true);
    const [statement, setStatement] = useState(null);
    const [payments, setPayments] = useState(null);

    // User permissions
    const ViewAgencyStatement = useUserPermission('ViewAgencyStatement');
    const ViewClientStatement = useUserPermission('ViewClientStatement');
    const ViewModelStatement = useUserPermission('ViewModelStatement');
    const ViewServiceStatement = useUserPermission('ViewServiceStatement');
    const ViewAgencyCommission = useUserPermission('ViewAgencyCommission');
    const ViewAgencyFee = useUserPermission('ViewAgencyFee');
    const ViewMotherAgencyCommission = useUserPermission('ViewMotherAgencyCommission');

    // Determine if payments are needed for this statement type
    const needPayments = useMemo(() => {
        return statementType !== 'client';
    }, [statementType]);

    const [statBalances, setStatBalances] = useState(null);

    // Check if user has access to view this statement
    const statementAccess = useMemo(() => {
        switch (contactType) {
            case 'model':
                return ViewModelStatement && ViewAgencyCommission;
            case 'client':
                return ViewClientStatement && ViewAgencyFee;
            case 'agency':
                return ViewAgencyStatement && ViewMotherAgencyCommission;
            case 'service':
                return ViewServiceStatement;
            default:
                return false;
        }
    }, [
        contactType,
        ViewModelStatement,
        ViewClientStatement,
        ViewAgencyStatement,
        ViewServiceStatement,
        ViewAgencyCommission,
        ViewAgencyFee,
        ViewMotherAgencyCommission,
    ]);

    const getStatBalances = async () => {
        if (contactType === 'agency') {
            Axios.get(`/customers/balancesummary/${contactID}`).then((res) => {
                setStatBalances(res);
            });
        } else {
            setStatBalances(null);
        }
    };

    // Fetch statement data
    const getStatement = async () => {
        dispatch({ type: 'SetLoading', value: true });
        setIsStatementFetching(true);

        try {
            // Fetch statement data
            const statementData = await Axios.get(apiUrl);
            setStatement(statementData);

            // Fetch payments if needed
            if (needPayments) {
                const paymentData = await Axios.get(paymentsApiUrl);
                setPayments(paymentData);
            } else {
                setPayments(null);
            }

            setIsStatementFetching(false);
            setIsInitialLoad(false);
        } catch (error) {
            console.error('Statement fetch error:', error);
            setIsStatementFetching(false);
            setIsInitialLoad(false);
        } finally {
            dispatch({ type: 'SetLoading', value: false });
        }
    };

    // Fetch statement on mount and when dependencies change
    useEffect(() => {
        if (contactID && apiUrl) {
            getStatement();
            getStatBalances();
        }
    }, [contactID, apiUrl, statementType]);

    // Handle reload requests
    useEffect(() => {
        if (reload) {
            getStatement();
            getStatBalances();
            dispatch({ type: 'StopReload' });
        }
    }, [reload]);

    // Render initial loading state (only on first load)
    if (isInitialLoad && isStatementFetching) {
        return (
            <Container>
                <NwCard title={'Statement'}>
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem 0' }}>
                        <Loading text="" textBlack />
                    </div>
                </NwCard>
            </Container>
        );
    }

    // Render access denied state
    if (!statementAccess) {
        return (
            <Container>
                <NwCard
                    title={'Statement'}
                    extra={
                        onClose && (
                            <NwIcon
                                pointer
                                icon={byPrefixAndName.fasl['times']}
                                onClick={onClose}
                                style={CloseIconStyle}
                            />
                        )
                    }
                >
                    <SystemMessage type="not-allowed" />
                </NwCard>
            </Container>
        );
    }

    // Render statement tabs (always show tabs, loading state is handled inside tab content)
    return (
        <Container>
            <StatementTabs
                statement={statement}
                payments={payments}
                onClose={onClose}
                isLoading={isStatementFetching}
                statBalances={statBalances}
            />
        </Container>
    );
};

export default StatementCard;
